import { useMemo } from 'react';

import isEqual from 'lodash/isEqual';

import { Autocomplete, mergeSx, SxProps } from '@cast/design-system';

import { useSearchCriterion, useSearchContext } from '@cast/shared-search-ui';

type Props<T extends Record<string, any>> = {
  prop: keyof T;
  filter?: (item: T) => boolean;
  placeholder?: string;
  sx?: SxProps;
  listSx?: SxProps;
  analyticsHandler?: (values: string[]) => void;
};

export const FilterWithUniqueValues = <T extends object>({
  prop,
  filter,
  placeholder,
  sx,
  listSx,
  analyticsHandler,
}: Props<T>) => {
  const searchContext = useSearchContext<T>();
  const { setValue, value, clear } = useSearchCriterion(prop as string);

  const _value = useMemo(() => {
    return filter ? value?.filter(filter) : value;
  }, [filter, value]);

  const uniqueValues = useMemo(() => {
    if (!prop) {
      return [];
    }
    const set = searchContext?.data?.reduce((acc, curr) => {
      if (curr[prop]) {
        acc.add(curr[prop] as unknown as string);
      }
      return acc;
    }, new Set<string>());
    if (set) {
      return Array.from(set).sort((a, b) => {
        if (a === 'default') {
          return 0;
        }
        if (b === 'default') {
          return 1;
        }
        return a.localeCompare(b);
      });
    }
    return [];
  }, [prop, searchContext?.data]);

  const handleChange = (values: string | string[] | null) => {
    if (values && Array.isArray(values) && values.length) {
      setValue?.(values);
      analyticsHandler?.(values);
    } else {
      clear?.();
    }
  };

  return (
    <Autocomplete
      className="tour-highlight"
      value={_value}
      options={uniqueValues}
      onChange={handleChange}
      inputText={placeholder}
      checkable
      multiple
      isOptionEqualToValue={isEqual}
      sx={mergeSx({ width: '100%' }, sx)}
      listSx={listSx}
    />
  );
};
